import React, { type ReactNode, useMemo } from "react";

import clsx from "clsx";

import { useDesignElementManager } from "@hooks/use-design-element-manager/use-design-element-manager";
import { type GetCampaign_me_adObject } from "@north-beam/nb-client-side";

import { type Level } from "../../types";

function getTotalAds(adsets: { ads: any[] }[], adset: any) {
  let total = 0;

  if (adset) {
    total += adset.ads.length;
  } else {
    for (const a of adsets) {
      total += a.ads.length;
    }
  }

  return total;
}

function findAdset(adObject: GetCampaign_me_adObject, adObjectLevel: Level) {
  if (adObjectLevel === "campaign") {
    return null;
  } else if (adObjectLevel === "adset") {
    return adObject.hierarchy.adsets.find((e: any) => e.id === adObject.id);
  } else {
    for (const adset of adObject.hierarchy.adsets) {
      const ad = adset.ads.find((e: any) => e.id === adObject.id);

      if (ad) {
        return adset;
      }
    }
  }
}

interface Props {
  adObject: GetCampaign_me_adObject;
  adObjectLevel: Level;
  platformLabel: string;
  onChangeLevel(id: string): void;
}

export const Hierarchy = ({
  adObject,
  adObjectLevel,
  platformLabel,
  onChangeLevel,
}: Props) => {
  const hierarchy = adObject.hierarchy;
  const adset = useMemo(
    () => findAdset(adObject, adObjectLevel),
    [adObject, adObjectLevel],
  );
  const adsetsCount = hierarchy.adsets.length;
  const adsCount = useMemo(
    () => getTotalAds(hierarchy.adsets, adset),
    [hierarchy, adset],
  );
  const { designElementManager } = useDesignElementManager();
  const icon = designElementManager
    .getDesignElement(platformLabel)
    .getComponent({ className: "mr-2", size: "24px" });

  return (
    <div className="mt-8">
      <h3 className="text-lg font-bold">Hierarchy</h3>
      <Entry label="PLATFORM" title={platformLabel} icon={icon} />
      <Entry
        isActive={adObjectLevel === "campaign"}
        indent={1}
        label="CAMPAIGN"
        title={hierarchy.name}
        onClick={
          adObjectLevel === "campaign"
            ? undefined
            : () => onChangeLevel(hierarchy.id)
        }
      />
      {(adObjectLevel === "adset" || adObjectLevel === "ad") && adset && (
        <Entry
          isActive={adObjectLevel === "adset"}
          indent={2}
          label="ADSET"
          title={adset.name}
          onClick={
            adObjectLevel === "adset"
              ? undefined
              : () => onChangeLevel(adset.id)
          }
        />
      )}
      {adObjectLevel === "ad" && (
        <Entry isActive={true} indent={3} label="AD" title={adObject.name} />
      )}
      {adObjectLevel === "campaign" && (
        <Entry isDashed indent={2} label="ADSETS" title={adsetsCount} />
      )}
      {(adObjectLevel === "campaign" || adObjectLevel === "adset") && (
        <Entry isDashed indent={3} label="ADS" title={adsCount} />
      )}
    </div>
  );
};

const Entry = ({
  isActive = false,
  isDashed = false,
  indent = 0,
  label,
  title,
  icon,
  onClick,
}: {
  isActive?: boolean;
  isDashed?: boolean;
  isInteractive?: boolean;
  indent?: number;
  label: string;
  title: string | number;
  icon?: ReactNode;
  onClick?(): void;
}) => (
  <div className="flex items-center mt-2" onClick={onClick}>
    {new Array(indent).fill(null).map((e, i) => {
      return indent - 1 === i ? (
        <i
          key={i}
          className="shrink-0 fa-regular fa-sharp fa-arrow-turn-down-right text-gray-1 px-3.5"
        />
      ) : (
        <div key={i} className="shrink-0  px-5" />
      );
    })}
    <div
      className={clsx(
        "flex-1 flex items-center text-xs border rounded py-1.5 px-3 truncate",
        {
          "border-gray-1": !isActive,
          "hover:cursor-pointer hover:border-interstellar-1": onClick,
          "border-cosmic": isActive,
          "border-dashed": isDashed,
        },
      )}
    >
      {icon}
      <div className="truncate">
        <div className="text-xxxs text-gray+1">{label}</div>
        <div
          className={clsx("font-bold mt-0.5 truncate", {
            "text-gray+1": isDashed,
          })}
        >
          {title}
        </div>
      </div>
    </div>
  </div>
);
